import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiUsers, FiBox, FiBriefcase, FiDollarSign, FiTerminal, FiCpu,
  FiChevronRight, FiTrendingUp, FiActivity, FiArrowUpRight, FiShield
} from 'react-icons/fi';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function ExecDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    jobsCount: 0,
    customersCount: 0,
    employeesCount: 0,
    productsCount: 0,
    completedJobsCount: 0,
    totalRevenue: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // โหลดสถิติจริงจากฐานข้อมูลหลังบ้าน
    Promise.all([
      fetch(`${API_URL}?action=get_all_jobs`).then(res => res.json()).catch(() => ({ data: [] })),
      fetch(`${API_URL}?action=get_customers`).then(res => res.json()).catch(() => ({ data: [] })),
      fetch(`${API_URL}?action=get_employees`).then(res => res.json()).catch(() => ({ data: [] })),
      fetch(`${API_URL}?action=get_products`).then(res => res.json()).catch(() => ({ data: [] }))
    ]).then(([jobsData, customersData, employeesData, productsData]) => {
      const allJobs = jobsData.data || [];
      const allCustomers = customersData.data || [];
      const allEmployees = employeesData.data || [];
      const allProducts = productsData.data || [];

      const completedJobs = allJobs.filter(j => j.status === 'completed');
      const revenue = completedJobs.reduce((sum, j) => sum + Number(j.price || 0), 0);

      setStats({
        jobsCount: allJobs.length,
        customersCount: allCustomers.length,
        employeesCount: allEmployees.length,
        productsCount: allProducts.length,
        completedJobsCount: completedJobs.length,
        totalRevenue: revenue
      });
      setIsLoading(false);
    }).catch(err => {
      console.error(err);
      setIsLoading(false);
    });
  }, []);

  const departments = [
    {
      id: 'customers',
      name: 'แผนกดูแลลูกค้า & คิวช่าง (CRM)',
      desc: 'ติดตามงานนัดหมาย ซ่อมแซม ติดตั้ง และการอนุมัติคิวจองล้างแอร์จากหน้าเว็บหลัก',
      icon: FiUsers,
      color: 'indigo',
      badge: `${stats.jobsCount} งานนัดหมายสะสม`,
      link: '/admin/data/customers',
      metrics: [
        { label: 'งานเสร็จสมบูรณ์', value: `${stats.completedJobsCount} คิว` },
        { label: 'รายชื่อลูกค้า CRM', value: `${stats.customersCount} คน` }
      ]
    },
    {
      id: 'stock',
      name: 'แผนกจัดการคลังสินค้า & สต๊อก',
      desc: 'ควบคุมสต๊อกเครื่องปรับอากาศ อะไหล่สเปก บันทึกการเบิกอะไหล่ และสถิติสต๊อกฝ่ายขาย',
      icon: FiBox,
      color: 'emerald',
      badge: `${stats.productsCount} รายการแอร์ในคลัง`,
      link: '/admin/products',
      metrics: [
        { label: 'มูลค่าแอร์ในระบบ', value: '฿' + (stats.productsCount * 14500).toLocaleString() },
        { label: 'คลังสินค้าออนไลน์', value: 'พร้อมขาย' }
      ]
    },
    {
      id: 'hr',
      name: 'แผนกฝ่ายบุคคล (HR & KPI)',
      desc: 'บันทึกประวัติพนักงาน ควบคุมการขาดลาสาย ลงเวลาทำงาน และประเมินผลงานช่าง (KPI)',
      icon: FiBriefcase,
      color: 'purple',
      badge: `${stats.employeesCount} รายชื่อพนักงาน`,
      link: '/admin/hr',
      metrics: [
        { label: 'แผนกปฏิบัติการ', value: '5 แผนก' },
        { label: 'ประเมินผลงาน KPI', value: 'มีผลบังคับใช้' }
      ]
    },
    {
      id: 'finance',
      name: 'แผนกวิเคราะห์การเงิน & บัญชี',
      desc: 'ตรวจสอบประวัติงาน รายรับสะสม ยอดจองล่วงหน้า และใบเสร็จ/ใบกำกับภาษีทั้งหมด',
      icon: FiDollarSign,
      color: 'amber',
      badge: `฿${stats.totalRevenue.toLocaleString()} รายรับจริง`,
      link: '/admin/finance',
      metrics: [
        { label: 'ประมาณคอมมิชชัน 5%', value: '฿' + (stats.totalRevenue * 0.05).toLocaleString() },
        { label: 'ออกใบเสร็จด่วน', value: 'รองรับ' }
      ]
    },
    {
      id: 'marketing',
      name: 'แผนกแชทบอท & การตลาด AI',
      desc: 'เข้าควบคุมบอทวิจัย Ruby, บอท Jasper คิดคอนเทนต์, รันเวิร์กโฟลว์การตลาด และระบบ Auto Post',
      icon: FiCpu,
      color: 'cyan',
      badge: '4 AI Agents ออนไลน์',
      link: '/admin/ai-workflow',
      metrics: [
        { label: 'ความเร็วบอท SQL', value: '8ms (Active)' },
        { label: 'แผนงานเวิร์กโฟลว์', value: 'อัตโนมัติ' }
      ]
    },
    {
      id: 'it',
      name: 'แผนกไอที & ความมั่นคงปลอดภัย',
      desc: 'เฝ้าระวังภัยคุกคาม แบล็คลิสต์ IP บุกรุก ตรวจสุขภาพฐานข้อมูล MySQL และดูระบบการทำงาน Logs',
      icon: FiShield,
      color: 'rose',
      badge: 'Firewall Active',
      link: '/admin/logs',
      metrics: [
        { label: 'Log Security Audit', value: 'ปลอดภัย 100%' },
        { label: 'อัตราการบล็อก IP', value: 'อัตโนมัติ' }
      ]
    }
  ];

  return (
    <div className="space-y-6 pb-20 animate-fade-in text-gray-800 dark:text-gray-100 font-sans text-left">
      
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-gray-850 dark:text-white flex items-center gap-2">
          🏢 แดชบอร์ดวิเคราะห์ระดับบริหาร (Executive Dashboard)
        </h1>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          ศูนย์บัญชาการกลางสำหรับผู้บริหารและ IT ตรวจสอบสถานะการทำงาน ประสิทธิภาพ และรายรับของทุกแผนกในบริษัทได้ในหนึ่งเดียว
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64 text-indigo-500 animate-pulse font-bold text-sm">
          กำลังประมวลผลข้อมูลทุกแผนก...
        </div>
      ) : (
        <>
          {/* Quick Metrics Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-4 rounded-xl shadow-sm">
              <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">รายรับสะสมฝ่ายบริหาร</div>
              <div className="text-xl font-black text-gray-850 dark:text-white">฿{stats.totalRevenue.toLocaleString()}</div>
            </div>
            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-4 rounded-xl shadow-sm">
              <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">งานนัดหมายช่างสะสม</div>
              <div className="text-xl font-black text-gray-850 dark:text-white">{stats.jobsCount} คิวงาน</div>
            </div>
            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-4 rounded-xl shadow-sm">
              <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">ยอดลูกค้าสะสมใน CRM</div>
              <div className="text-xl font-black text-gray-850 dark:text-white">{stats.customersCount} คน</div>
            </div>
            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-4 rounded-xl shadow-sm">
              <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">จำนวนบุคลากรทั้งหมด</div>
              <div className="text-xl font-black text-gray-850 dark:text-white">{stats.employeesCount} คน</div>
            </div>
          </div>

          {/* Department Control Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {departments.map((dept) => {
              const IconComponent = dept.icon;
              return (
                <div 
                  key={dept.id} 
                  className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-5 rounded-2xl shadow-sm flex flex-col justify-between hover:border-indigo-500 dark:hover:border-indigo-500/50 transition-all duration-300"
                >
                  <div>
                    {/* Header line */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`p-2 rounded-xl text-indigo-600 dark:text-indigo-400 bg-indigo-500/10`}>
                          <IconComponent size={18} />
                        </span>
                        <h3 className="text-xs font-bold text-gray-800 dark:text-white">{dept.name}</h3>
                      </div>
                      <span className="text-[8px] bg-indigo-500/10 text-indigo-500 px-2 py-0.5 rounded-full font-bold">
                        {dept.badge}
                      </span>
                    </div>

                    {/* Description */}
                    <p className="text-[10px] text-gray-550 dark:text-gray-400 leading-relaxed min-h-[40px] mb-4 text-left">
                      {dept.desc}
                    </p>

                    {/* Metrics grid */}
                    <div className="grid grid-cols-2 gap-2 bg-gray-50 dark:bg-[#1b2132]/20 p-2.5 rounded-xl mb-4">
                      {dept.metrics.map((m, idx) => (
                        <div key={idx} className="text-left">
                          <span className="text-[7.5px] uppercase font-bold text-gray-450 dark:text-gray-500 block">{m.label}</span>
                          <span className="text-[10px] font-black text-gray-700 dark:text-gray-200">{m.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Open Department button */}
                  <button 
                    onClick={() => navigate(dept.link)}
                    className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 rounded-xl text-[10px] transition-all flex items-center justify-center gap-1 shadow-sm shadow-indigo-600/10"
                  >
                    เข้าสู่แผนกและตั้งค่าระบบ <FiArrowUpRight size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

    </div>
  );
}
